# WHAT A CRASH SCREEN SHOULD DO NEXT — card 6ab166a3a4d698cda2b1fa90.
#
# Owner, 2026-09-21: "We need a ticket for a new crash screen. The one we have
# now is inadequate given our new invention of crash logs. We should show a
# pretty screen or funny animation and then refresh, if refresh crashes again
# we redirect to dashboard. What we have there now is really bad. A clear and
# refresh button though is necessary because if its a bad login, we can have
# them clear localstorage and relogin."
#
# WHAT THE OLD SCREEN WAS FOR, AND WHY IT IS NOT NEEDED. It showed an apology,
# the browser and OS, and "Show Error Details" with the stack behind it — all
# of it written so that a family could read our diagnostics down the phone to
# support. Since 2026-09-18 the boundary files a CrashLog row itself
# (crash_reporter.report, card 6aad7bb96e6a1e18a090353b) carrying the message,
# the stack, the component stack, the route, the device, the console tail and
# who the family is. We already have everything the screen was asking a child
# to transcribe. What is left for the screen to do is get them back into the
# app.
#
# THE ESCALATION, and why it has three steps rather than two.
#
#   refresh    First crash. The commonest crash we see is a transient — an
#              aborted read, a chunk that 404'd across a client point, a stale
#              bundle meeting a new server — and a reload is what fixes all
#              three. Nobody has to be told anything.
#   dashboard  It crashed again right after that reload, so whatever is wrong
#              is not transient and it is attached to THIS screen. Land on the
#              shell's own entry point and let its boot routing choose where
#              the family belongs. (Deliberately the shell's home rather than a
#              hardcoded `#/dashboard`: card 6ab16e9c is a crash whose route
#              WAS `#/dashboard`, and each shell already decides this properly
#              at boot.)
#   stop       The dashboard landing crashed too. Reloading again would be a
#              loop with a child inside it, so the screen stops moving and
#              offers the ONE button a person can act on: Log Out and Refresh
#              — one thing, since a38170d. It clears the session and LEAVES THE
#              URL BEHIND; see clearAndRefresh at the foot of this file for why
#              it cannot be a reload (card 6ab35fca4f3ebf539a382155).
#
# THE GUARD IS PER TAB AND IT EXPIRES. sessionStorage, for the same reason
# client_version's reload guard uses it: this is about THIS tab's recovery, and
# a second tab deserves its own. It expires after RECOVERY_WINDOW_MS because a
# crash an hour from now is a new incident, not the second half of this one;
# and the boundary clears it outright once the app has been up and quiet for
# HEALTHY_MS, so a good reload does not leave the next unrelated crash starting
# half-way up the escalation.
#
# STORAGE IS ALLOWED TO BE ABSENT — private windows, blocked site data and
# embedded WebViews all throw on access. A tab that cannot remember gets
# `refresh` every time, which is the behaviour of the escalation's first step
# and is the safe end to fail towards: at worst a reload, never a loop, because
# reloadOnceFor's own latch and the browser are both still in the way.

GUARD_KEY = 'elfeetCrashRecovery'

# How long a recovery step stays "the one we just took".
export RECOVERY_WINDOW_MS = 60000

export REFRESH = 'refresh'
export DASHBOARD = 'dashboard'
export STOP = 'stop'

# What follows what. A phase that is not one of ours reads as the end of the
# line rather than as the beginning, because an unrecognised value is more
# likely a newer bundle's word than a fresh start.
NEXT = {}
NEXT[REFRESH] = DASHBOARD
NEXT[DASHBOARD] = STOP
NEXT[STOP] = STOP

# Where each shell's boot routing starts. `crash_reporter.install(name)` is
# given these names by the three shells, so this is the one mapping and it
# cannot drift from whoever is reporting.
HOMES = {app: '/app', teacher: '/teacher', org: '/org'}

export homeFor = (appName) -> HOMES[appName] ? '/app'

storage = (win) ->
  try win?.sessionStorage ? null
  catch e then null

export readRecovery = (win, now = Date.now()) ->
  try
    raw = storage(win)?.getItem GUARD_KEY
    return null unless raw
    parsed = JSON.parse raw
    return null unless parsed? and typeof parsed is 'object'
    return null unless typeof parsed.at is 'number'
    return null if (now - parsed.at) > RECOVERY_WINDOW_MS or (now - parsed.at) < 0
    parsed
  catch e
    null

export clearRecovery = (win) ->
  try
    storage(win)?.removeItem GUARD_KEY
    true
  catch e
    false

# Decide this crash's step AND record it, in one call.
#
# Written BEFORE anything is acted on, the way client_version writes its reload
# guard first: a recovery that beats its own bookkeeping is a recovery loop. A
# tab whose storage refuses the write still gets a phase back and still acts on
# it — one reload with no memory is better than a crash screen that does
# nothing — and the browser's own reload behaviour is the backstop.
export beginRecovery = (win, now = Date.now()) ->
  previous = readRecovery win, now
  phase = if previous? then (NEXT[previous.phase] ? STOP) else REFRESH
  try
    storage(win)?.setItem GUARD_KEY, JSON.stringify({phase: phase, at: now})
  catch e
    null
  phase

# Do it. Separated from the decision so the decision is drivable without a
# location, and so the boundary can put a screen in front of a person for a
# moment before this runs.
#
# The dashboard landing is a hash/query change within the same document, which
# on its own does not load anything — so the assignment is followed by a
# reload when the path has not actually changed. That is the whole of the
# subtlety here, and it is why this is a function rather than two lines in a
# component.
export performRecovery = (phase, win, appName = 'app') ->
  return false unless win?.location?
  try
    if phase is DASHBOARD
      home = homeFor appName
      samePath = "#{win.location.pathname ? ''}" is home
      win.location.href = home
      win.location.reload() if samePath
      true
    else if phase is REFRESH
      win.location.reload()
      true
    else
      false
  catch e
    false

# The bad-login way out the owner asked to keep: drop everything this browser
# remembers about the session and start over. Not `clearUser()` — a login bad
# enough to crash the shell is exactly the case where we do not trust the
# app's own idea of what to clear.
#
# IT CANNOT BE A RELOAD, AND THAT IS THE WHOLE OF CARD 6ab35fca4f3ebf539a382155.
# A reload re-runs the page at the SAME URL, and a crashing sign-in link IS the
# URL: app-route.coffee installs `params.access_token` at MODULE SCOPE, before
# React starts and before the replaceState that strips it. So
# /app?access_token=<bad>&uid=<id> came straight back — click the one button,
# reload, the same token re-installed, the same crash. The one button on the
# screen did not log the child out, which is the only thing it is there to do.
#
# So: go to the shell's own home path, with NO QUERY. Nothing is lost by
# leaving the URL behind — the query is the credential that crashed us and the
# hash is the route we crashed on.
#
# AND sessionStorage GOES TOO, so the next boot re-arms nothing. Two keys
# outlive the clear otherwise (loopback_api.coffee): StaleAccessTokenLink, set
# by app-route when the URL's token could not authenticate, and AttemptedQRLogin.
# Both are about the session we are throwing away. The escalation guard lives
# there too; clearRecovery stays below so a tab whose storage refuses `clear()`
# still forgets the escalation.
#
# WHICH SHELL — read from the PATH, not from the reporter. crash_reporter's
# appName is only right once `install()` has run, and install() runs after
# loadSchema() resolves — so a boot crash from a bad sign-in link, the exact
# crash this card is about, is the one where the reporter still says its
# default 'app'. A teacher clicking the button would have been logged out onto
# the parent shell. The path already knows; appName is the fallback for a path
# that names no shell we serve.
export shellFromPath = (pathname) ->
  return null unless typeof pathname is 'string'
  for own name, home of HOMES
    return name if pathname is home or pathname.indexOf("#{home}/") is 0
  null

export clearAndRefresh = (win, appName = 'app') ->
  try
    win?.localStorage?.clear()
  catch e
    null
  try
    win?.sessionStorage?.clear()
  catch e
    null
  clearRecovery win
  return false unless win?.location?
  try
    home = homeFor (shellFromPath(win.location.pathname) ? appName)
    # Same rule as performRecovery's home landing, and one more term: an
    # assignment that only drops the FRAGMENT is not a navigation, so it would
    # leave the child on the dead React tree. A query means the assignment does
    # load, and a reload on top of it would race the navigation we just started.
    stationary = "#{win.location.pathname ? ''}" is home and "#{win.location.search ? ''}" is ''
    win.location.href = home
    win.location.reload() if stationary
    true
  catch e
    false
